import { cloneElement, useRef, useState } from 'react';
import './desktop-raid-control-layout.css';

const CONTEXT_SPLIT_STORAGE_KEY = 'dororaid:raid-control-context-orders-share';
const DEFAULT_CONTEXT_ORDERS_SHARE = 72;

function clampContextShare(value, panelHeight = 0) {
  const height = Math.max(0, Number(panelHeight) || 0);
  const minimumByHeight = height > 0 ? (120 / height) * 100 : 20;
  const minimum = Math.max(20, Math.min(40, minimumByHeight));
  const maximum = Math.min(80, Math.max(60, 100 - minimumByHeight));
  return Math.min(maximum, Math.max(minimum, Number(value) || DEFAULT_CONTEXT_ORDERS_SHARE));
}

function initialContextShare() {
  if (typeof window === 'undefined') return DEFAULT_CONTEXT_ORDERS_SHARE;
  try {
    return clampContextShare(Number(window.localStorage.getItem(CONTEXT_SPLIT_STORAGE_KEY)));
  } catch {
    return DEFAULT_CONTEXT_ORDERS_SHARE;
  }
}

/** Desktop pane order follows member selection, practice, conversation, live orders. */
export function DesktopRaidControlLayout({ members, timeline, orders, practice, recentResults }) {
  const contextPanelRef = useRef(null);
  const resizingRef = useRef(false);
  const [resultsCollapsed, setResultsCollapsed] = useState(true);
  const [ordersShare, setOrdersShare] = useState(initialContextShare);

  const updateContextShare = (clientY) => {
    const panel = contextPanelRef.current;
    if (!panel) return;
    const rect = panel.getBoundingClientRect();
    if (!rect.height) return;
    const next = ((Number(clientY) - rect.top) / rect.height) * 100;
    setOrdersShare(clampContextShare(next, rect.height));
  };

  const saveContextShare = (value = ordersShare) => {
    try {
      window.localStorage.setItem(CONTEXT_SPLIT_STORAGE_KEY, String(Math.round(value * 10) / 10));
    } catch {
      // Resizing still works when storage is unavailable.
    }
  };

  const finishResize = (event) => {
    if (!resizingRef.current) return;
    resizingRef.current = false;
    event.currentTarget.releasePointerCapture?.(event.pointerId);
    saveContextShare();
  };

  const handleSeparatorKeyDown = (event) => {
    if (!['ArrowUp', 'ArrowDown', 'Home', 'End'].includes(event.key)) return;
    event.preventDefault();
    const panelHeight = contextPanelRef.current?.getBoundingClientRect().height || 0;
    const next = event.key === 'Home'
      ? 20
      : event.key === 'End'
        ? 80
        : ordersShare + (event.key === 'ArrowUp' ? -4 : 4);
    const clamped = clampContextShare(next, panelHeight);
    setOrdersShare(clamped);
    saveContextShare(clamped);
  };

  return <div className={`raid-control-desktop-panes${practice ? ' has-practice' : ''}`}>
    {members}
    {practice ? <aside className="raid-control-practice-panel" aria-label="멤버 모의전">{practice}</aside> : null}
    {timeline}
    <aside
      ref={contextPanelRef}
      className={`raid-control-context-panel${resultsCollapsed ? ' results-collapsed' : ''}`}
      aria-label="공격 현황과 최근 실전"
      style={{ '--raid-control-context-orders-share': `${ordersShare}%` }}
    >
      <div className="raid-control-context-orders">{orders}</div>
      {!resultsCollapsed ? <div
        className="raid-control-context-resizer"
        role="separator"
        tabIndex={0}
        aria-label="진행 중 공격과 최근 실전 높이 조절"
        aria-orientation="horizontal"
        aria-valuemin={20}
        aria-valuemax={80}
        aria-valuenow={Math.round(ordersShare)}
        onPointerDown={(event) => {
          if (event.button !== 0) return;
          resizingRef.current = true;
          event.currentTarget.setPointerCapture?.(event.pointerId);
          updateContextShare(event.clientY);
          event.preventDefault();
        }}
        onPointerMove={(event) => {
          if (!resizingRef.current) return;
          updateContextShare(event.clientY);
        }}
        onPointerUp={finishResize}
        onPointerCancel={finishResize}
        onKeyDown={handleSeparatorKeyDown}
      ><span aria-hidden="true" /></div> : null}
      <div className="raid-control-context-results">{recentResults ? cloneElement(recentResults, { collapsed: resultsCollapsed, onToggle: () => setResultsCollapsed(value => !value) }) : null}</div>
    </aside>
  </div>;
}
